import { useState, FormEvent } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import InputAdornment from '@mui/material/InputAdornment';
import CircularProgress from '@mui/material/CircularProgress';
import Link from '@mui/material/Link';
import StorefrontIcon from '@mui/icons-material/Storefront';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { requestPasswordReset } from '../utils/auth';
import { palette, typography, theme } from '../theme/colors';

export const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await requestPasswordReset(email);
      setSuccess(true);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to send reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <Box
        sx={{
          display: 'flex',
          minHeight: '100vh',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: palette.background,
          p: { xs: 3, sm: 6 },
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 400, textAlign: 'center' }}>
          {/* Logo */}
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: theme.radius.xl,
              background: `linear-gradient(135deg, ${palette.success} 0%, ${palette.successDark} 100%)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 3,
            }}
          >
            <CheckCircleOutlineIcon sx={{ color: '#fff', fontSize: 32 }} />
          </Box>

          <Typography
            variant="h4"
            sx={{
              fontFamily: typography.fontFamily.display,
              fontWeight: typography.fontWeight.bold,
              color: palette.textPrimary,
              mb: 2,
            }}
          >
            Check Your Email
          </Typography>

          <Alert
            severity="success"
            sx={{
              mb: 3,
              borderRadius: theme.radius.lg,
              backgroundColor: palette.successMuted || '#ecfdf5',
              color: palette.success,
              border: `1px solid ${palette.success}30`,
              '& .MuiAlert-icon': {
                color: palette.success,
              },
            }}
          >
            If an account exists with this email, you will receive a password reset link shortly.
          </Alert>

          <Typography
            variant="body2"
            sx={{ color: palette.textSecondary, mb: 4 }}
          >
            The link will expire in 30 minutes. Check your spam folder if you don't see the email.
          </Typography>

          <Link
            component={RouterLink}
            to="/login"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              color: palette.primary,
              textDecoration: 'none',
              fontWeight: typography.fontWeight.medium,
              '&:hover': {
                textDecoration: 'underline',
              },
            }}
          >
            <ArrowBackIcon sx={{ fontSize: 18 }} />
            Back to login
          </Link>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        minHeight: '100vh',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: palette.background,
        position: 'relative',
        overflow: 'hidden',
        p: { xs: 3, sm: 6 },
      }}
    >
      {/* Background Gradient Mesh */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background: theme.gradients.mesh,
          opacity: 0.5,
        }}
      />

      {/* Animated Gradient Orb */}
      <Box
        sx={{
          position: 'absolute',
          top: '20%',
          right: '20%',
          width: 300,
          height: 300,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${palette.primary}20 0%, transparent 70%)`,
          filter: 'blur(60px)',
          animation: 'float 8s ease-in-out infinite',
          '@keyframes float': {
            '0%, 100%': { transform: 'translate(0, 0)' },
            '50%': { transform: 'translate(30px, -30px)' },
          },
        }}
      />

      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          width: '100%',
          maxWidth: 400,
          backgroundColor: palette.surface,
          borderRadius: theme.radius.xl,
          border: `1px solid ${palette.border}`,
          p: { xs: 3, sm: 4 },
        }}
      >
        {/* Logo */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            mb: 4,
          }}
        >
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: theme.radius.lg,
              background: theme.gradients.primary,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <StorefrontIcon sx={{ color: palette.textOnPrimary, fontSize: 24 }} />
          </Box>
          <Typography
            variant="h5"
            sx={{
              fontFamily: typography.fontFamily.display,
              fontWeight: typography.fontWeight.bold,
              color: palette.textPrimary,
            }}
          >
            Nexera
          </Typography>
        </Box>

        <Typography
          variant="h4"
          sx={{
            fontFamily: typography.fontFamily.display,
            fontWeight: typography.fontWeight.bold,
            color: palette.textPrimary,
            mb: 1,
          }}
        >
          Reset Password
        </Typography>

        <Typography
          variant="body2"
          sx={{ color: palette.textSecondary, mb: 4 }}
        >
          Enter your email address and we'll send you a link to reset your password.
        </Typography>

        {error && (
          <Alert
            severity="error"
            sx={{
              mb: 3,
              borderRadius: theme.radius.lg,
              backgroundColor: palette.errorMuted,
              color: palette.error,
              border: `1px solid ${palette.error}30`,
              '& .MuiAlert-icon': {
                color: palette.error,
              },
            }}
          >
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="caption"
              sx={{
                color: palette.textSecondary,
                fontWeight: typography.fontWeight.medium,
                mb: 1,
                display: 'block',
              }}
            >
              Email Address
            </Typography>
            <TextField
              fullWidth
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="Enter your email"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailOutlinedIcon sx={{ color: palette.textMuted, fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: palette.surfaceElevated,
                  borderRadius: theme.radius.lg,
                  '& fieldset': {
                    borderColor: palette.border,
                  },
                  '&:hover fieldset': {
                    borderColor: palette.borderLight,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: palette.primary,
                  },
                },
                '& .MuiInputBase-input': {
                  color: palette.textPrimary,
                  '&::placeholder': {
                    color: palette.textMuted,
                    opacity: 1,
                  },
                },
              }}
            />
          </Box>

          <Button
            fullWidth
            type="submit"
            variant="contained"
            size="large"
            disabled={loading}
            endIcon={!loading && <ArrowForwardIcon />}
            sx={{
              py: 1.75,
              background: theme.gradients.primary,
              color: palette.textOnPrimary,
              fontWeight: typography.fontWeight.semibold,
              fontSize: typography.fontSize.md,
              borderRadius: theme.radius.lg,
              mb: 3,
              '&:hover': {
                transform: 'translateY(-2px)',
              },
              '&:disabled': {
                background: palette.border,
                color: palette.textMuted,
              },
            }}
          >
            {loading ? (
              <CircularProgress size={24} sx={{ color: palette.textOnPrimary }} />
            ) : (
              'Send Reset Link'
            )}
          </Button>
        </form>

        <Box sx={{ textAlign: 'center' }}>
          <Link
            component={RouterLink}
            to="/login"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              color: palette.primary,
              textDecoration: 'none',
              fontWeight: typography.fontWeight.medium,
              '&:hover': {
                textDecoration: 'underline',
              },
            }}
          >
            <ArrowBackIcon sx={{ fontSize: 18 }} />
            Back to login
          </Link>
        </Box>
      </Box>
    </Box>
  );
};
